<template>
  <form>
    <FormHeader v-if="title" :title="title" @cancel="onCancel" @submit="onSubmit"/>

    <div class="space-y-12 mt-5">
      <div class="border-b border-gray-900/10 pb-12">
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Identification de l'organisation") }}</h3>
        <FormIdentificationTypeField v-model="identificationType" field-path="attributes.identificationType" :field-label="$gettext('Moyen d\'identification')" :field-attrs="identificationTypeAttrs" :errors="errors" />
        <FormTextField v-model="companyNumber" field-path="attributes.companyNumber" :field-label="$gettext('Numéro d\'entreprise')" :field-attrs="companyNumberAttrs" :errors="errors" />
        <div class="grid grid-cols-3 items-start gap-4">
          <div class="col-span-1 mt-0 col-start-2">
            <button @click.prevent="preFill(companyNumber)" class="mr-5 inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              {{ $gettext("Pré-remplir") }}
            </button>
          </div>
        </div>
      </div>
      <div class="border-b border-gray-900/10 pb-12">
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Adresse de l'organisation") }}</h3>
        <FormAddressAutocompleteField @update:address="handleAddressUpdate" v-model="addressStreet" field-path="attributes.addressStreet" :field-label="$gettext('Rue et numéro')" :field-attrs="addressStreetAttrs" :errors="errors" />
        <FormTextField v-model="addressZipcode" field-path="attributes.addressZipcode" :field-label="$gettext('Code postal')" :field-attrs="addressZipcodeAttrs" :errors="errors" />
        <FormTextField v-model="addressCity" field-path="attributes.addressCity" :field-label="$gettext('Ville')" :field-attrs="addressCityAttrs" :errors="errors" />
        <FormSelectField v-model="addressCountryId" field-path="relationships.addressCountry.data.id" :field-label="$gettext('Pays')" :field-attrs="addressCountryIdAttrs" :errors="errors" :options="countryOptions" />
      </div>
      <div class="border-b border-gray-900/10 pb-12">
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Détails de l'organisation") }}</h3>
        <FormTextField v-model="name" field-path="attributes.name" :field-label="$gettext('Dénomination sociale')" :field-attrs="nameAttrs" :errors="errors" />
        <FormSelectField v-model="companyTypeId" field-path="relationships.companyType.data.id" :field-label="$gettext('Forme juridique')" :field-attrs="companyTypeIdAttrs" :errors="errors" :options="companyTypeOptions" :disabled="!addressCountryId" />
        <FormTextField v-model="vatNumber" field-path="attributes.vatNumber" :field-label="$gettext('Numéro de TVA')" :field-attrs="vatNumberAttrs" :errors="errors" :enabled="false"/>

        <FormDateField v-model="establishmentDate" field-path="attributes.establishmentDate" :field-label="$gettext('Date de création')" :field-attr="establishmentDateAttrs" :errors="errors" />
        <FormTextField v-model="website" field-path="attributes.website" :field-label="$gettext('Site web')" :field-attrs="websiteAttrs" :errors="errors" />
        <!-- PEP? -->
      </div>
      <div class="border-b border-gray-900/10 pb-12">
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Activité économique de l'organisation") }}</h3>
        <FormSelectField v-model="industrySectorId" field-path="relationships.industrySector.data.id" :field-label="$gettext('Secteur d\'activité')" :field-attrs="industrySectorIdAttrs" :errors="errors" :options="industrySectorOptions" />
      </div>
      <div class="border-b border-gray-900/10 pb-12">
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Détails de la personne signataire") }}</h3>
        <FormSelectField v-model="isSignatoryLegalRepresentative" field-path="attributes.isSignatoryLegalRepresentative" :field-label="$gettext('La personne signataire est-elle le représentant légal ?')" :field-attrs="isSignatoryLegalRepresentativeAttrs" :options="[{value: true, label: 'Oui'}, { value: false, label: 'Non, mais elle détient une procuration'}]"  :errors="errors" />
        <FormCustomerComboboxField v-model="signatories" :physical-person-only=true field-path="relationships.signatories.data" :field-label="$gettext('Identification de la personne signataire')" :field-attrs="signatoriesAttrs" :errors="errors">
        </FormCustomerComboboxField>
      </div>
      <div class="border-b border-gray-900/10 pb-12">
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Bénéficiaires effectifs") }}</h3>
        <FormCustomerComboboxField
          v-model="companyUltimateBeneficialOwners"
          :physical-person-only="true"
          :ubo="true"
          field-path="relationships.companyUltimateBeneficialOwners.data"
          :field-label="$gettext('Liste des bénéficiaires effectifs')"
          :field-attrs="companyUltimateBeneficialOwnersAttrs"
          :errors="errors"
        >
          <template #actions="{ customer, index }">
            <div>
              <select
                v-model="customer.relationships.ultimateBeneficialOwnerControlType.data.id"
                :class="[
                  'block w-92 rounded-md border bg-white px-2.5 py-1.5 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500',
                  errors[`relationships.companyUltimateBeneficialOwners.data[${index}].relationships.ultimateBeneficialOwnerControlType.data.id`] ? 'border-red-500' : 'border-gray-300'
                ]"
              >
                <option :value="null" disabled>{{ $gettext("Sélectionner un type de contrôle") }}</option>
                <option
                  v-for="option in ultimateBeneficialOwnerControlTypeOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
              <p v-if="errors[`relationships.companyUltimateBeneficialOwners.data[${index}].relationships.ultimateBeneficialOwnerControlType.data.id`]" class="text-sm text-red-600">
                {{ errors[`relationships.companyUltimateBeneficialOwners.data[${index}].relationships.ultimateBeneficialOwnerControlType.data.id`] }}
              </p>
            </div>

            <FormPercentageField
              v-if="ultimateBeneficialOwnerControlTypeOptions.find(o => o.value === customer.relationships.ultimateBeneficialOwnerControlType.data.id)?.percentageRequired"
              v-model="customer.attributes.ultimateBeneficiaryOwnerControlPercentage"
              field-path="attributes.ultimateBeneficiaryOwnerControlPercentage"
              :field-attrs="companyUltimateBeneficialOwnersAttrs"
              :errors="errors"
            />
          </template>
        </FormCustomerComboboxField>
      </div>
      <div class="border-b border-gray-900/10 pb-2">
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Liens officiels de l'entreprise") }}</h3>
        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2">
          <KboLink v-if="companyNumber" :enterpriseNumber="companyNumber" />
        </div>
        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2">
          <EjusticeLink v-if="companyNumber" :enterpriseNumber="companyNumber" />
        </div>
        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2">
          <FednotLink v-if="companyNumber" :enterpriseNumber="companyNumber" />
        </div>
        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2">
          <NbbLink v-if="companyNumber" :enterpriseNumber="companyNumber" />
        </div>
      </div>
      <div id="identity-documents-section" class="border-b border-gray-900/10 pb-12">
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Documents à importer") }}</h3>
        <FormDocumentsField
          v-model="companyDocuments"
          category-name="signatoryDocuments"
          field-path="relationships.companyDocuments.data"
          :field-label="$gettext('Document d\'identité du signataire à la mission')"
          :field-description="$gettext('Représentant légal ou mandataire')"
          :field-attrs="companyDocumentsAttrs" :errors="errors"/>
        <FormDocumentsField
          v-model="companyDocuments"
          category-name="legalRepresentatives"
          field-path="relationships.companyDocuments.data"
          :field-label="$gettext('Statuts coordonnés')"
          :field-description="$gettext('Disponibles sur à la BCE ou au Moniteur Belge pour les entreprises belges. Pour les entreprises étrangères, l’équivalent en droit national')"
          :field-attrs="companyDocumentsAttrs" :errors="errors"/>
        <FormDocumentsField
          v-model="companyDocuments"
          category-name="physicalPersonEngagement"
          field-path="relationships.companyDocuments.data"
          :field-label="$gettext('Disposition régissant le pouvoir d\'engager la personne morale')"
          :field-description="$gettext('Pour les entreprises belges, extrait BCE. Pour les entreprises étrangères, l\'équivalent en droit national')"
          :field-attrs="companyDocumentsAttrs"
          :errors="errors"/>
        <FormDocumentsField
          v-model="companyDocuments"
          category-name="administrators"
          field-path="relationships.companyDocuments.data"
          :field-label="$gettext('Liste des administrateurs')"
          :field-description="$gettext('Pour les entreprises belges, extrait BCE. Pour les entreprises étrangères, l\'équivalent en droit national')"
          :field-attrs="companyDocumentsAttrs"
          :errors="errors"/>
        <FormDocumentsField
          v-model="companyDocuments"
          category-name="uboRegisterExtract"
          field-path="relationships.companyDocuments.data"
          :field-label="$gettext('Extrait registre UBO')"
          :field-description="$gettext('Fourni par l\'entreprise')"
          :field-attrs="companyDocumentsAttrs"
          :errors="errors"/>
      </div>
    </div>
    <FormFooter @cancel="onCancel" @submit="onSubmit"/>
  </form>
</template>

<script setup>
import { useCountryStore } from "@/stores/country.js";
import { useCompanyTypeStore } from "@/stores/companyType.js";
import { useIndustrySectorStore } from "@/stores/industrySector";
import { useCompanyStore } from "@/stores/company";
import { useForm } from "vee-validate";
import * as yup from "yup";
import { computed, onMounted, watch } from "vue";
import FormHeader from "@/components/form-fields/FormHeader.vue";
import FormFooter from "@/components/form-fields/FormFooter.vue";
import FormAddressAutocompleteField from "@/components/form-fields/FormAddressAutocompleteField.vue";
import FormTextField from "@/components/form-fields/FormTextField.vue";
import FormDateField from "@/components/form-fields/FormDateField.vue";
import FormSelectField from "@/components/form-fields/FormSelectField.vue";
import FormIdentificationTypeField from "@/components/form-fields/FormIdentificationTypeField.vue";
import FormCustomerComboboxField from "../form-fields/FormCustomerComboboxField.vue";
import { useGettext } from "vue3-gettext";
import FormDocumentsField from "../form-fields/FormDocumentsField.vue";
import FormPercentageField from "@/components/form-fields/FormPercentageField.vue";
import { useUltimateBeneficialOwnerControlTypeStore } from "@/stores/ultimateBeneficialOwnerControlType";
import KboLink from "@/components/companies/links/KboLink.vue";
import FednotLink from "@/components/companies/links/FednotLink.vue";
import EjusticeLink from "@/components/companies/links/EjusticeLink.vue";
import NbbLink from "@/components/companies/links/NbbLink.vue";

const { $gettext } = useGettext();

const countryStore = useCountryStore();
const companyTypeStore = useCompanyTypeStore();
const industrySectorStore = useIndustrySectorStore();
const companyStore = useCompanyStore();
const ultimateBeneficialOwnerControlTypeStore = useUltimateBeneficialOwnerControlTypeStore();

onMounted(async () => {
  if (ultimateBeneficialOwnerControlTypeStore.all.length === 0) {
    await ultimateBeneficialOwnerControlTypeStore.fetchAll();
  }
  await ultimateBeneficialOwnerControlTypeStore.subscribeToChanges();
});

const props = defineProps(["company", "title"]);
const emits = defineEmits(["submit", "cancel"]);

const countryOptions = computed(() =>
  countryStore.sortedCountries.map((country) => ({
    value: country.id,
    label: country.attributes.name,
  }))
);
const industrySectorOptions = computed(() =>
  industrySectorStore.industrySectors.map((industrySector) => ({
    value: industrySector.id,
    label: industrySector.attributes.name,
  }))
);
const companyTypeOptions = computed(() =>
  companyTypeStore.companyTypes.map((companyType) => ({
    value: companyType.id,
    label: companyType.attributes.name,
  }))
);
const ultimateBeneficialOwnerControlTypeOptions = computed(() =>
  ultimateBeneficialOwnerControlTypeStore.all.map((ultimateBeneficialOwnerControlType) => ({
    value: ultimateBeneficialOwnerControlType.id,
    label: ultimateBeneficialOwnerControlType.attributes.name,
    percentageRequired: ultimateBeneficialOwnerControlType.attributes.percentageRequired
  }))
);
const validationSchema = yup.object({
  attributes: yup.object({
    identificationType: yup.string().required($gettext("est obligatoire")),
    companyNumber: yup.number().typeError($gettext("doit être un nombre")).required($gettext("est obligatoire")),
    name: yup.string().required($gettext("est obligatoire")),
    establishmentDate: yup.date().required($gettext("est obligatoire")),
    isSignatoryLegalRepresentative: yup.boolean().required($gettext("est obligatoire")),
    addressStreet: yup.string().required($gettext("est obligatoire")),
    addressCity: yup.string().required($gettext("est obligatoire")),
    addressZipcode: yup.number().required($gettext("est obligatoire"))
  }),
  relationships: yup.object({
    addressCountry: yup.object({
      data: yup.object({
        id: yup.string().required($gettext("est obligatoire"))
      })
    }),
    companyType: yup.object({
      data: yup.object({
        id: yup.string().required($gettext("est obligatoire"))
      })
    }),
    industrySector: yup.object({
      data: yup.object({
        id: yup.string().required($gettext("est obligatoire"))
      })
    }),
    signatories: yup.object({
      data: yup.array(
        yup.object({
          id: yup.string().required($gettext("est obligatoire"))
        })
      )
    }),
    companyUltimateBeneficialOwners: yup.object({
      data: yup.array().of(
        yup.object({
          relationships: yup.object({
            ultimateBeneficialOwnerControlType: yup.object({
              data: yup.object({
                id: yup.string().nullable().required($gettext("est obligatoire"))
              })
            })
          }),
          attributes: yup.object({
            ultimateBeneficiaryOwnerControlPercentage: yup.string().nullable()
          })
        })
      )
    })
  })
});

const { errors, handleSubmit, defineField, setValues, setFieldValue } = useForm({
  validationSchema: validationSchema,
  initialValues: props.company
});

const [name, nameAttrs] = defineField("attributes.name");
const [addressStreet, addressStreetAttrs] = defineField("attributes.addressStreet");
const [addressCity, addressCityAttrs] = defineField("attributes.addressCity");
const [addressZipcode, addressZipcodeAttrs] = defineField("attributes.addressZipcode");
const [companyNumber, companyNumberAttrs] = defineField("attributes.companyNumber");
const [establishmentDate, establishmentDateAttrs] = defineField("attributes.establishmentDate");
const [identificationType, identificationTypeAttrs] = defineField("attributes.identificationType");
const [vatNumber, vatNumberAttrs] = defineField("attributes.vatNumber");
const [website, websiteAttrs] = defineField("attributes.website");
const [addressCountryId, addressCountryIdAttrs] = defineField("relationships.addressCountry.data.id");
const [companyTypeId, companyTypeIdAttrs] = defineField("relationships.companyType.data.id");
const [isSignatoryLegalRepresentative, isSignatoryLegalRepresentativeAttrs] = defineField("attributes.isSignatoryLegalRepresentative");
const [industrySectorId, industrySectorIdAttrs] = defineField("relationships.industrySector.data.id");
const [signatories, signatoriesAttrs] = defineField("relationships.signatories.data");
const [companyUltimateBeneficialOwners, companyUltimateBeneficialOwnersAttrs] = defineField("relationships.companyUltimateBeneficialOwners.data");
const [companyDocuments, companyDocumentsAttrs] = defineField("relationships.companyDocuments.data");

const stripCustomerObjects = (customers) => {
  if (!Array.isArray(customers)) return customers;
  return customers.map(({ id, type }) => ({ id, type }));
};

const stripUboObjects = (ubos) => {
  if (!Array.isArray(ubos)) return ubos;
  return ubos.map(({ id, type, attributes, relationships }) => ({
    id,
    type,
    attributes: {
      ultimateBeneficiaryOwnerControlPercentage: attributes?.ultimateBeneficiaryOwnerControlPercentage
    },
    relationships: {
      ultimateBeneficialOwnerControlType: relationships?.ultimateBeneficialOwnerControlType
    }
  }));
};

const onFormSubmit = ((values, actions) => {
const payload = {
    type: "company",
    ...values,
    relationships: {
      ...values.relationships,
      signatories: {
        ...values.relationships?.signatories,
        data: stripCustomerObjects(values.relationships?.signatories?.data)
      },
      companyUltimateBeneficialOwners: {
        ...values.relationships?.companyUltimateBeneficialOwners,
        data: stripUboObjects(values.relationships?.companyUltimateBeneficialOwners?.data)
      }
    }
  };
  emits("submit", payload, actions);
});

const handleAddressUpdate = (address) => {
  const components = address?.address_components || [];

  const streetNumber = components.find(c => c.types.includes("street_number"))?.long_name || "";
  const streetName = components.find(c => c.types.includes("route"))?.long_name || "";
  const city = components.find(c => c.types.includes("locality"))?.long_name || "";
  const postalCode = components.find(c => c.types.includes("postal_code"))?.long_name || "";
  const countryCode = components.find(c => c.types.includes("country"))?.short_name || "";

  setFieldValue("attributes.addressStreet", `${streetName} ${streetNumber}`.trim());
  setFieldValue("attributes.addressCity", city);
  setFieldValue("attributes.addressZipcode", postalCode);
  const matchedCountry = countryStore.sortedCountries.find(
    (country) => country.attributes.code === countryCode
  );
  if (matchedCountry) {
    setFieldValue("relationships.addressCountry.data.id", matchedCountry.id);
  } else {
    setFieldValue("relationships.addressCountry.data.id", null); // Or handle unknown country
  }
};

const onSubmit = (() => {
  handleSubmit(onFormSubmit)();
});

const onCancel = (() => {
  emits("cancel");
});

watch(() => props.company, (newCompany) => {
  setValues(newCompany);
});

watch(addressCountryId, async (newCountryId) => {
  if (newCountryId) {
    await companyTypeStore.fetchCompanyTypes(newCountryId);
  }
});

const preFill = (newCompanyNumber) => {
  companyStore.prefillCompany(newCompanyNumber, (response) => {
    setFieldValue("attributes.name", response.attributes.name);
    setFieldValue("attributes.addressCity", response.attributes.addressCity);
    setFieldValue("attributes.addressStreet", response.attributes.addressStreet);
    setFieldValue("attributes.addressZipcode", response.attributes.addressZipcode);
    setFieldValue("attributes.vatNumber", response.attributes.vatNumber);
    setFieldValue("attributes.establishmentDate", response.attributes.establishmentDate);
    setFieldValue("relationships.addressCountry.data.id", response.attributes.addressCountryId);
  }, (errors) => {
    console.error(errors);
  });
};

</script>
